import {
  FC,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
} from 'react';
import styled from 'styled-components';
import { GlobalStyles, ThemeProvider, Typography } from '@mui/material';
import {
  AssociateSetupContext,
  AuthenticationContext,
} from '@vs-online-only-app/state';
import { CenteredLoader, theme } from '@vs-online-only-app/ui';
import AppSidebar from './AppSidebar';
import { TopBar } from '../TopBar';
import SnackNotifier from '../SnackNotifier';

export enum AppRoutes {
  Home = '/',
  Chats = '/chats',
  Contacts = '/contacts',
  Products = '/products',
  Tutorials = '/tutorials',
  Settings = '/settings',
}

const isStandalone = window === window.top;

const RootWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  background: ${({ theme }) => theme.palette.background.default};
  height: 100vh;
  position: relative;
`;

const PageWrapper = styled.div`
  display: grid;
  grid-template-columns: ${isStandalone ? 'auto' : ''} 1fr;
  overflow: auto;
`;

const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, logoutStatus } = useContext(AuthenticationContext);
  const { hasCompletedProfileSetup, hasCompletedIntroModules } = useContext(
    AssociateSetupContext,
  );

  const renderPage = useCallback(() => {
    if (['loading', 'success'].includes(logoutStatus)) {
      return (
        <CenteredLoader>
          <Typography variant="body1">Logging out</Typography>
        </CenteredLoader>
      );
    }

    const displayCompleteLayout =
      hasCompletedProfileSetup && hasCompletedIntroModules;
    if (isAuthenticated && displayCompleteLayout) {
      return (
        <PageWrapper>
          <AppSidebar />
          <SnackNotifier />
          {children}
        </PageWrapper>
      );
    }

    return children;
  }, [
    logoutStatus,
    isAuthenticated,
    children,
    hasCompletedProfileSetup,
    hasCompletedIntroModules,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles styles={{ '*': { boxSizing: 'border-box' } }} />
      <RootWrapper>
        <TopBar />
        <Suspense
          fallback={
            <CenteredLoader>
              <Typography variant="body1">Loading Layout</Typography>
            </CenteredLoader>
          }
        >
          {renderPage()}
        </Suspense>
      </RootWrapper>
    </ThemeProvider>
  );
};

export default AppLayout;
